import {
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { KYDTicketType } from "@common/interfaces/KYDTicket";
import { ErrorMessage } from "@hookform/error-message";
import { useContext, useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { KYDGuestlistDisplayState } from "@common/interfaces/KYDEvent";
import { createDialogContext, DialogProvider } from "../../../../Common/Dialog";
import { isTicketTypeSystem } from "../../../../utils";

export const GuestlistDialogContext = createDialogContext();

type GueslistAllocationFields = KYDGuestlistDisplayState;

const GuestlistDialog = ({
  onSave,
  allocation,
  allocationErrors,
  isLoading = false,
  ticket_types = [],
}: {
  isLoading: boolean;
  allocationErrors: Record<string, boolean | string | null>;
  ticket_types: KYDTicketType[];
  onSave: (data: GueslistAllocationFields) => void;
  allocation?: GueslistAllocationFields;
}) => {
  const { isOpen, close } = useContext(GuestlistDialogContext);

  const allocationsWithoutSystem = allocation.allocations.filter(
    (a) => !isTicketTypeSystem(a.ticket_type_id)
  );

  const ticketTypesWithoutGuestlist = ticket_types?.filter((tt) => {
    if (!allocationsWithoutSystem.length) {
      return true;
    }

    const hasAllocation = allocationsWithoutSystem.find(
      (allocation) => allocation.ticket_type_id === tt.id
    );

    if (hasAllocation) {
      return false;
    }

    return true;
  });

  const {
    register,
    handleSubmit,
    setError,
    reset,
    clearErrors,
    formState: { errors, isValid },
    control,
    watch,
  } = useForm<GueslistAllocationFields>({
    mode: "all",
    criteriaMode: "all",
    values: {
      allocations: [...allocationsWithoutSystem].concat(
        ticketTypesWithoutGuestlist.map((tt) => ({
          id: tt.id,
          ticket_type_id: tt.id,
          name: "",
          allocation: 0,
          assigned: 0,
          remaining: 0,
          is_processing: false,
        }))
      ),
    },
    disabled: isLoading,
  });

  const { fields } = useFieldArray({ control, name: "allocations" });
  const editingAllocations = watch("allocations");

  useEffect(() => {
    if (allocationErrors) {
      Object.keys(allocationErrors).forEach((key) => {
        if (allocationErrors[key]) {
          //@ts-ignore
          setError(key, { message: allocationErrors[key] });
        } else {
          //@ts-ignore
          clearErrors(key);
        }
      });
    }
  }, [allocationErrors]);

  const getErrorMessageFor = (field, index, property) => {
    if (!field || !field[index] || !field[index][property]) return;

    return field[index][property].message;
  };

  const onClose = () => {
    close();
    reset();
  };

  const getAllocation = (index: number) => {
    return editingAllocations.at(index);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent
          mb={[0, "auto"]}
          borderBottomLeftRadius={[0, "md"]}
          borderBottomRightRadius={[0, "md"]}
        >
          <ModalHeader paddingTop={4}>
            <VStack gap={0} width={"full"}>
              <Text w="full">Guest List Allocation</Text>
            </VStack>
          </ModalHeader>
          <ModalBody textAlign={"left"}>
            <HStack>
              <VStack w="full">
                <Text w="full">Ticket Type</Text>
              </VStack>
              <VStack w="full" justifyContent={"flex-start"}>
                <Text w="full">Number of tickets</Text>
              </VStack>
            </HStack>
            {fields.map((field, index) => (
              <HStack gap={4} my={1} key={field.id}>
                <VStack gap={0} alignItems={"flex-start"} w="full">
                  <Text my={2} w="full" fontWeight={"bold"}>
                    {
                      ticket_types.find(
                        (tt) => tt.id === getAllocation(index)?.ticket_type_id
                      )?.name
                    }
                  </Text>
                </VStack>

                <VStack gap={0} w="full">
                  <Input
                    type="number"
                    placeholder="Quantity"
                    isInvalid={
                      !!getErrorMessageFor(
                        errors.allocations,
                        index,
                        `allocation`
                      )
                    }
                    {...register(`allocations.${index}.allocation`, {
                      required: "Quantity is required",
                      min: {
                        value: getAllocation(index)?.assigned || 0,
                        message: `Has to be greater or equal than already assigned tickets number (${
                          getAllocation(index)?.assigned
                        })`,
                      },
                      max: {
                        value: 100,
                        message: `Can not be more than 100 tickets`,
                      },
                      valueAsNumber: true,
                    })}
                  />
                  <ErrorMessage
                    errors={errors}
                    name={`allocations.${index}.allocation`}
                    render={({ message }) => (
                      <Text fontSize="small" w="full">
                        {message}
                      </Text>
                    )}
                  />
                </VStack>
              </HStack>
            ))}
          </ModalBody>
          <ModalFooter>
            <Stack direction={["column-reverse", "row-reverse"]} width="100%">
              <Button
                key={"cancel"}
                isLoading={isLoading}
                isDisabled={isLoading}
                w={"auto"}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                key={"submit"}
                variant="kydDark"
                w={"auto"}
                isLoading={isLoading}
                isDisabled={isLoading || !isValid}
                onClick={handleSubmit((data) => onSave(data))}
              >
                Save
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export const withGuestlistDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={GuestlistDialogContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default GuestlistDialog;
